// Sistema de sonidos minimalista con Web Audio API (Sprint 8).
//
// - Sin libs externas: usamos `AudioContext` directamente.
// - SFX sintéticos (oscilladores + envelopes) — livianos y sin assets.
// - Settings: on/off persistido en localStorage.
// - Singleton store (no provider) para no agregar context hell.
//
// API:
//   const { play } = useSounds();
//   play('card-play');
//   play('enemy-died');

import { create } from 'zustand';
import { useEffect } from 'react';

export type SoundName =
  | 'card-play'
  | 'enemy-hit'
  | 'enemy-died'
  | 'life-lost'
  | 'draw'
  | 'victory'
  | 'defeat'
  | 'click';

interface SoundSpec {
  /** Frecuencia base en Hz. */
  freq: number;
  /** Duración total en ms. */
  duration: number;
  /** Tipo de onda. */
  type: OscillatorType;
  /** Volumen (0–1). */
  gain: number;
  /** Si el tono "desciende" (frecuencia final = freq * factor). */
  glide?: number;
}

const SOUNDS: Record<SoundName, SoundSpec> = {
  'card-play': { freq: 440, duration: 100, type: 'sine', gain: 0.15 },
  'enemy-hit': { freq: 220, duration: 150, type: 'square', gain: 0.18, glide: 0.5 },
  'enemy-died': { freq: 660, duration: 250, type: 'sawtooth', gain: 0.12, glide: 1.5 },
  'life-lost': { freq: 200, duration: 400, type: 'sawtooth', gain: 0.2, glide: 0.3 },
  draw: { freq: 800, duration: 80, type: 'sine', gain: 0.1 },
  victory: { freq: 523, duration: 500, type: 'sine', gain: 0.2 },
  defeat: { freq: 110, duration: 600, type: 'sawtooth', gain: 0.18, glide: 0.5 },
  click: { freq: 1000, duration: 30, type: 'square', gain: 0.08 },
};

const STORAGE_KEY = 'regicide:sound-on';

interface SoundStoreState {
  enabled: boolean;
  toggle: () => void;
}

export const useSoundStore = create<SoundStoreState>((set) => ({
  enabled:
    typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) !== 'false' : true,
  toggle: () => {
    set((s) => {
      const next = !s.enabled;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, String(next));
      }
      return { enabled: next };
    });
  },
}));

let _ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (_ctx) return _ctx;
  const AC =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  _ctx = new AC();
  return _ctx;
}

function playOnce(spec: SoundSpec): void {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    void ctx.resume();
  }
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = spec.type;
  osc.frequency.setValueAtTime(spec.freq, ctx.currentTime);
  if (spec.glide) {
    osc.frequency.exponentialRampToValueAtTime(
      spec.freq * spec.glide,
      ctx.currentTime + spec.duration / 1000,
    );
  }
  // Envelope: ataque rápido + decay.
  gain.gain.setValueAtTime(0, ctx.currentTime);
  gain.gain.linearRampToValueAtTime(spec.gain, ctx.currentTime + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + spec.duration / 1000);
  osc.connect(gain).connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + spec.duration / 1000);
}

/** Hook que retorna función `play(name)` que respeta el setting enabled. */
export function useSounds(): {
  play: (name: SoundName) => void;
  enabled: boolean;
} {
  const enabled = useSoundStore((s) => s.enabled);
  return {
    enabled,
    play: (name) => {
      if (!enabled) return;
      const spec = SOUNDS[name];
      if (!spec) return;
      playOnce(spec);
    },
  };
}

/** Hidrata `enabled` desde localStorage en cliente. */
export function SoundHydrator(): null {
  useEffect(() => {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== null) {
        useSoundStore.setState({ enabled: stored !== 'false' });
      }
    }
  }, []);
  return null;
}
